/**
 * useResolveThriveAvatars — the photos of the candidates on screen, by Thrive
 * contact id (epic 2026-10-06-search-candidates-reports, sub-task 05; polling
 * with backoff since epic 2026-10-08-candidates-polish-thrive-avatar-worker,
 * sub-task 03).
 *
 * The page renders first with initials; this hook then asks the store which of
 * the rows' contacts have a Thrive photo and the photos fill in — the
 * prototype's behaviour. Order per contact:
 *
 *   1. the THRIVE photo, by contact id (`POST /avatars/resolve-thrive`);
 *   2. for a contact Thrive has NO photo for (`none`), the LINKEDIN photo by
 *      profile URL — today's store, `POST /avatars/resolve` — when it holds a
 *      fresh row;
 *   3. otherwise nothing, and the `Avatar` primitive keeps the monogram.
 *
 * `pending` ids are the ones the store did not know: the first call enqueued
 * one fetch each (one Thrive read per contact in `thrive_avatar_worker`), and
 * this hook POLLS with `enqueue_missing: false`, so nothing is fetched twice,
 * for as long as anything on the page is pending, the page is mounted and the
 * document is visible (React Query skips interval fetches in a hidden tab).
 * The gaps grow — `POLL_BACKOFF_SECONDS`, 3, 5, 8, 13, then every 20 s at the
 * API's 3-second hint — so a slow worker (a big page, a Thrive 429 waiting
 * out its visibility timeout) still fills the page without a reload, at one
 * cheap table read per gap. A FAILED poll moves to the next, longer gap
 * rather than stopping; only a failed FIRST call, with nothing known to be
 * pending, leaves the page on its initials until the next mount.
 *
 * "Enqueue once" is per contact id, not per page: the ids already asked with
 * `enqueue_missing: true` are remembered per QueryClient with the time they
 * were asked, so a sort, a filter or a page turn that brings a still-pending
 * id back asks for it with `enqueue_missing: false` (a second request when a
 * page mixes new and already-asked ids). An id asked more than
 * `ENQUEUE_REARM_MS` ago may be enqueued again — by the next poll, so an id
 * whose message was lost (or dead-lettered) is re-asked while the page stays
 * open, and the backoff starts over from its first gap. An answer with
 * `pending` ids is never cached as if it were final (`staleTime` is 0 while
 * anything is pending), so a remount asks again.
 *
 * A photo older than 30 days comes back as `photo` (the old face) while the
 * worker refreshes it; nothing is pending, so nothing polls — the new photo,
 * under a new `checked_at`, shows on a later visit.
 *
 * The URL of every stored key, Thrive or LinkedIn, comes from ONE
 * `useStoredImageUrls` call: CloudFront (`/media/<key>?v=<checked_at>`) when
 * the media session serves avatars, a presigned GET otherwise — the same
 * resolver People Lists and the company lists use, no second code path.
 *
 * Fail-SOFT: avatars are cosmetic. A failed resolve or presign leaves the
 * affected rows on their initials and logs once; nothing throws into the tab.
 */

import type { ThriveAvatarRef, ThriveAvatarResolveResponse } from "@nu-assist/contracts";
import { type QueryClient, skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";

import type { AvatarsClient } from "../../clients/avatars.client";
import { useStoredImageUrls } from "../../features/media/useStoredImageUrls";

/** Anything that carries a Thrive contact id and, for the fallback, a
 *  LinkedIn URL — a `SearchCandidateRow`, or the panel's one candidate. */
export interface HasThriveContact {
  contact_id?: number | string | null;
  linkedin_url?: string | null;
}

/** The gaps between polls while anything is `pending`, in seconds AS THE API'S
 *  3-SECOND HINT GIVES THEM: the hint is the first gap and the rest scale
 *  with it (`pollDelayMs`). The last gap repeats until nothing is pending. A
 *  page of 50 unknown ids is mostly written in 3–5 s, so one or two polls is
 *  the common case; the tail is for a slow worker, not a give-up point. */
export const POLL_BACKOFF_SECONDS = [3, 5, 8, 13, 20] as const;

/** How long an id asked with `enqueue_missing: true` is not asked that way
 *  again: a message lost or dead-lettered is re-asked after this, by a poll.
 *  Well past the time a healthy worker takes, short of a session. */
export const ENQUEUE_REARM_MS = 15 * 60 * 1000;

/** How long a resolved page with no `pending` id is reused. */
const SETTLED_STALE_MS = 30 * 60 * 1000;

export const THRIVE_AVATARS_QUERY_KEY = "thrive-avatars";
export const LINKEDIN_AVATARS_QUERY_KEY = "linkedin-avatars-for-candidates";

/** The rows' contact ids as the wire wants them: strings, de-duplicated,
 *  SORTED so the query key is the same set in any row order. */
export function contactIdsOf(rows: readonly HasThriveContact[]): string[] {
  const ids = new Set<string>();
  for (const row of rows) {
    if (row.contact_id === null || row.contact_id === undefined) continue;
    const id = String(row.contact_id);
    if (/^[0-9]+$/.test(id)) ids.add(id);
  }
  return [...ids].sort((a, b) => a.localeCompare(b));
}

/** Whether a resolve answer still has ids the store is fetching. */
export function hasPending(results: Record<string, ThriveAvatarRef> | undefined): boolean {
  return Object.values(results ?? {}).some((r) => r.status === "pending");
}

/**
 * The gap before the next poll: step `attempt` of `POLL_BACKOFF_SECONDS`
 * (the last step repeats), scaled so the first step is the server's hint —
 * 3, 5, 8, 13, 20, 20, … seconds at the API's 3. `attempt` counts the polls
 * since the last enqueue plus the consecutive failed ones.
 */
export function pollDelayMs(attempt: number, hintSeconds: number | null | undefined): number {
  const base = POLL_BACKOFF_SECONDS[0];
  const last = POLL_BACKOFF_SECONDS.length - 1;
  const step = POLL_BACKOFF_SECONDS[Math.min(Math.max(attempt, 0), last)] ?? base;
  return ((hintSeconds ?? base) * step * 1000) / base;
}

/** One answer for a page of ids, plus how many polls it took since the last
 *  enqueue — the backoff counts from there, not from the query's life. */
interface ThriveRound extends ThriveAvatarResolveResponse {
  polls: number;
}

/** Contact id → epoch ms it was last asked with `enqueue_missing: true`, per
 *  QueryClient (one per app session; a fresh one per test). */
const askedAtByClient = new WeakMap<QueryClient, Map<string, number>>();

function askedAtFor(queryClient: QueryClient): Map<string, number> {
  let askedAt = askedAtByClient.get(queryClient);
  if (!askedAt) {
    askedAt = new Map();
    askedAtByClient.set(queryClient, askedAt);
  }
  return askedAt;
}

/**
 * Resolve `ids`, enqueueing only the ones not asked within `ENQUEUE_REARM_MS`:
 * one request when every id is new (or none is), two when the page mixes both.
 */
async function resolveRound(
  client: AvatarsClient,
  ids: readonly string[],
  askedAt: Map<string, number>,
  previous: ThriveRound | undefined,
  signal: AbortSignal
): Promise<ThriveRound> {
  const now = Date.now();
  const toEnqueue = ids.filter((id) => {
    const at = askedAt.get(id);
    return at === undefined || now - at >= ENQUEUE_REARM_MS;
  });
  const enqueueSet = new Set(toEnqueue);
  const toPoll = ids.filter((id) => !enqueueSet.has(id));
  const [enqueued, polled] = await Promise.all([
    toEnqueue.length > 0
      ? client.resolveThrive(toEnqueue, { enqueueMissing: true, signal }).then((answer) => {
          // Marked as soon as THIS request succeeds — the server enqueued them
          // even if the poll request beside it fails.
          for (const id of toEnqueue) askedAt.set(id, now);
          return answer;
        })
      : undefined,
    toPoll.length > 0 ? client.resolveThrive(toPoll, { enqueueMissing: false, signal }) : undefined,
  ]);
  const results = { ...polled?.results, ...enqueued?.results };
  const hints = [enqueued, polled]
    .map((r) => r?.refresh_after_seconds)
    .filter((s): s is number => typeof s === "number");
  return {
    results,
    refresh_after_seconds: hints.length > 0 ? Math.min(...hints) : null,
    polls: toEnqueue.length > 0 ? 0 : (previous?.polls ?? -1) + 1,
  };
}

/**
 * Contact id (as a string) → displayable photo URL, for the rows that have one.
 * A missing id means initials. `client` undefined (a tree without one — unit
 * tests of the table, isolated stories) resolves nothing and asks nothing.
 */
export function useResolveThriveAvatars(
  client: AvatarsClient | undefined,
  rows: readonly HasThriveContact[]
): ReadonlyMap<string, string> {
  const queryClient = useQueryClient();
  const contactIds = useMemo(() => contactIdsOf(rows), [rows]);
  const thriveKey = [THRIVE_AVATARS_QUERY_KEY, contactIds] as const;

  const askedAt = askedAtFor(queryClient);
  // Consecutive failed rounds of THIS page — React Query resets its own
  // `fetchFailureCount` at every fetch, so it cannot say "the third failure
  // in a row". Held by the hook (it dies with the tab) and keyed by page, and
  // a page's entry goes when the hook leaves it: never more than one entry
  // (PR #1024 iter 1, C-M8).
  const failures = useRef(new Map<string, number>()).current;
  const page = contactIds.join(",");
  useEffect(() => () => void failures.delete(page), [failures, page]);

  const thrive = useQuery({
    queryKey: thriveKey,
    queryFn:
      client && contactIds.length > 0
        ? async ({ signal }) => {
            try {
              const round = await resolveRound(
                client,
                contactIds,
                askedAt,
                queryClient.getQueryData<ThriveRound>(thriveKey),
                signal
              );
              failures.delete(page);
              return round;
            } catch (error) {
              failures.set(page, (failures.get(page) ?? 0) + 1);
              throw error;
            }
          }
        : skipToken,
    refetchInterval: (query) => {
      // Nothing known to be pending — settled, or a failed FIRST call — means
      // nothing to poll for. A failed poll keeps the last answer (`data`) and
      // waits one step longer than it would have.
      const data = query.state.data;
      if (!data || !hasPending(data.results)) return false;
      return pollDelayMs(data.polls + (failures.get(page) ?? 0), data.refresh_after_seconds);
    },
    // The default, stated: interval fetches skip a hidden document.
    refetchIntervalInBackground: false,
    // A settled page is good for the session: the rows the store holds do not
    // change under the reader. An answer with `pending` ids is never fresh, so
    // a remount asks again instead of trusting it.
    staleTime: (query) => (hasPending(query.state.data?.results) ? 0 : SETTLED_STALE_MS),
    retry: false,
  });

  const results = thrive.data?.results;

  // The LinkedIn fallback — ONLY for the contacts Thrive answered `none`, and
  // only those with a LinkedIn URL to ask by. Never for `photo` (there is a
  // better picture) and never while `pending` (the answer may become `photo`).
  const fallbackUrls = useMemo(() => {
    if (!results) return [] as string[];
    const urls = new Set<string>();
    for (const row of rows) {
      if (row.contact_id === null || row.contact_id === undefined || !row.linkedin_url) continue;
      if (results[String(row.contact_id)]?.status === "none") urls.add(row.linkedin_url);
    }
    return [...urls].sort((a, b) => a.localeCompare(b));
  }, [results, rows]);

  const linkedin = useQuery({
    queryKey: [LINKEDIN_AVATARS_QUERY_KEY, fallbackUrls],
    queryFn:
      client && fallbackUrls.length > 0
        ? ({ signal }) => client.resolveLinkedin(fallbackUrls, signal)
        : skipToken,
    staleTime: SETTLED_STALE_MS,
    retry: false,
  });

  // Every stored key on this page, Thrive and LinkedIn, through ONE resolver.
  // Versions carry the Thrive read's `checked_at` as `?v=` so a photo
  // re-fetched onto the same key is not served from the browser's cache of
  // the old one.
  const { s3Keys, versions } = useMemo(() => {
    const keys = new Set<string>();
    const versions = new Map<string, number>();
    for (const ref of Object.values(results ?? {})) {
      if (ref.status === "photo" && ref.s3_key) {
        keys.add(ref.s3_key);
        if (ref.checked_at) versions.set(ref.s3_key, ref.checked_at);
      }
    }
    for (const ref of Object.values(linkedin.data?.known ?? {})) {
      if (ref?.s3_key) keys.add(ref.s3_key);
    }
    return { s3Keys: [...keys].sort((a, b) => a.localeCompare(b)), versions };
  }, [results, linkedin.data]);

  const presign = useMemo(
    () => (client ? (keys: string[]) => client.presignAvatars(keys) : undefined),
    [client]
  );
  const { urls, error: presignError } = useStoredImageUrls(
    "avatars",
    s3Keys,
    presign,
    "avatar-presign",
    versions
  );

  useEffect(() => {
    const error = thrive.error ?? linkedin.error ?? presignError;
    if (error) {
      console.warn(
        `[useResolveThriveAvatars] photos unavailable for ${contactIds.length} contact(s); those rows render initials.`,
        error
      );
    }
  }, [thrive.error, linkedin.error, presignError, contactIds.length]);

  return useMemo(() => {
    const byContact = new Map<string, string>();
    if (!results) return byContact;
    const known = linkedin.data?.known ?? {};
    for (const row of rows) {
      if (row.contact_id === null || row.contact_id === undefined) continue;
      const id = String(row.contact_id);
      const ref = results[id];
      if (!ref) continue;
      if (ref.status === "photo" && ref.s3_key) {
        const url = urls.get(ref.s3_key);
        if (url) byContact.set(id, url);
      } else if (ref.status === "none" && row.linkedin_url) {
        const key = known[row.linkedin_url]?.s3_key;
        const url = key ? urls.get(key) : undefined;
        if (url) byContact.set(id, url);
      }
    }
    return byContact;
  }, [results, linkedin.data, rows, urls]);
}
